/**
 * Comprehensive Disease Prediction System Object
 * Multi-stage symptom questionnaire → Disease prediction
 * 
 * Flow:
 * 1. User selects body part/region
 * 2. Relevant symptom questions appear (10 questions per category)
 * 3. User answers questions (4-6 options per question)
 * 4. Analysis & prediction displayed
 * 
 * All data structure ready for ML model integration
 */

const DiseasePredictionSystem = {
  // ============================================
  // STAGE 1: BODY PART SELECTION
  // ============================================
  bodyParts: {
    head_neck: {
      id: 'head_neck',
      label: 'Head & Neck',
      description: 'Headaches, migraines, sore throat, facial pain',
      icon: 'head',
      relatedDiseases: ['Migraine', 'Tension Headache', 'Sinusitis', 'Meningitis', 'Common Cold'],
      symptomsCount: 10
    },
    chest_lungs: {
      id: 'chest_lungs',
      label: 'Chest & Lungs',
      description: 'Chest pain, cough, breathing difficulty, heart issues',
      icon: 'lungs',
      relatedDiseases: ['Asthma', 'Pneumonia', 'Bronchitis', 'Heart Disease', 'Pulmonary Embolism'],
      symptomsCount: 10
    },
    abdomen_digestive: {
      id: 'abdomen_digestive',
      label: 'Abdomen & Digestive',
      description: 'Stomach pain, nausea, diarrhea, indigestion',
      icon: 'stomach',
      relatedDiseases: ['Gastroenteritis', 'Ulcer', 'IBS', 'Appendicitis', 'Hepatitis'],
      symptomsCount: 10
    },
    limbs_joints: {
      id: 'limbs_joints',
      label: 'Limbs & Joints',
      description: 'Joint pain, muscle ache, swelling, mobility issues',
      icon: 'bones',
      relatedDiseases: ['Arthritis', 'Tendonitis', 'Gout', 'Fracture', 'Rheumatoid Arthritis'],
      symptomsCount: 10
    },
    skin_surface: {
      id: 'skin_surface',
      label: 'Skin & Surface',
      description: 'Rashes, itching, skin lesions, infections',
      icon: 'skin',
      relatedDiseases: ['Eczema', 'Psoriasis', 'Fungal Infection', 'Dermatitis', 'Scabies'],
      symptomsCount: 10
    },
    general_systemic: {
      id: 'general_systemic',
      label: 'General & Systemic',
      description: 'Fever, fatigue, chills, body ache, general malaise',
      icon: 'body',
      relatedDiseases: ['Flu', 'Common Cold', 'Fever', 'Malaria', 'Typhoid'],
      symptomsCount: 10
    }
  },

  // ============================================
  // STAGE 2: SYMPTOM QUESTIONS BY BODY PART
  // ============================================
  symptoms: {
    head_neck: {
      bodyPartId: 'head_neck',
      questions: [
        {
          id: 'h1',
          questionNumber: 1,
          text: 'What type of headache are you experiencing?',
          type: 'single_select',
          options: [
            { value: 'throbbing', label: 'Throbbing/Pulsating pain' },
            { value: 'sharp', label: 'Sharp, stabbing pain' },
            { value: 'dull', label: 'Dull, pressure-like pain' },
            { value: 'tension', label: 'Tension/tightness around head' }
          ],
          weight: 0.8
        },
        {
          id: 'h2',
          questionNumber: 2,
          text: 'Where is the pain located?',
          type: 'single_select',
          options: [
            { value: 'frontal', label: 'Front of head (forehead/eyes)' },
            { value: 'temporal', label: 'Sides of head (temples)' },
            { value: 'occipital', label: 'Back of head' },
            { value: 'vertex', label: 'Top of head' },
            { value: 'generalized', label: 'All over the head' }
          ],
          weight: 0.7
        },
        {
          id: 'h3',
          questionNumber: 3,
          text: 'What is the severity of your pain?',
          type: 'single_select',
          options: [
            { value: 'mild', label: 'Mild (1-3/10) - manageable' },
            { value: 'moderate', label: 'Moderate (4-6/10) - interferes with activities' },
            { value: 'severe', label: 'Severe (7-8/10) - difficult to function' },
            { value: 'unbearable', label: 'Unbearable (9-10/10) - disabling' }
          ],
          weight: 0.9
        },
        {
          id: 'h4',
          questionNumber: 4,
          text: 'How often does this headache occur?',
          type: 'single_select',
          options: [
            { value: 'first_time', label: 'First time experiencing this' },
            { value: 'occasional', label: 'Occasionally (few times a month)' },
            { value: 'frequent', label: 'Frequent (several times a week)' },
            { value: 'chronic', label: 'Chronic (daily or almost daily)' }
          ],
          weight: 0.75
        },
        {
          id: 'h5',
          questionNumber: 5,
          text: 'Are you experiencing any associated symptoms?',
          type: 'multiple_select',
          options: [
            { value: 'nausea', label: 'Nausea or vomiting' },
            { value: 'sensitivity_light', label: 'Sensitivity to light (photophobia)' },
            { value: 'sensitivity_sound', label: 'Sensitivity to sound (phonophobia)' },
            { value: 'aura', label: 'Visual disturbances/aura' },
            { value: 'none', label: 'None of the above' }
          ],
          weight: 0.85
        },
        {
          id: 'h6',
          questionNumber: 6,
          text: 'Have you had a sore throat or throat pain?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No sore throat' },
            { value: 'mild', label: 'Mild - slight discomfort' },
            { value: 'moderate', label: 'Moderate - painful to swallow' },
            { value: 'severe', label: 'Severe - very difficult to swallow' }
          ],
          weight: 0.7
        },
        {
          id: 'h7',
          questionNumber: 7,
          text: 'Do you have swollen glands or lymph nodes?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No swelling' },
            { value: 'barely_noticeable', label: 'Barely noticeable' },
            { value: 'visible', label: 'Visibly swollen' },
            { value: 'very_swollen', label: 'Very swollen and painful' }
          ],
          weight: 0.75
        },
        {
          id: 'h8',
          questionNumber: 8,
          text: 'Have you experienced neck stiffness or pain?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No neck pain' },
            { value: 'mild', label: 'Mild stiffness' },
            { value: 'moderate', label: 'Moderate - limited movement' },
            { value: 'severe', label: 'Severe - cannot touch chin to chest' }
          ],
          weight: 0.8
        },
        {
          id: 'h9',
          questionNumber: 9,
          text: 'Are you experiencing fever?',
          type: 'single_select',
          options: [
            { value: 'no_fever', label: 'No fever (normal temperature)' },
            { value: 'low_grade', label: 'Low-grade (99-101°F / 37.2-38.3°C)' },
            { value: 'moderate', label: 'Moderate (101-103°F / 38.3-39.4°C)' },
            { value: 'high', label: 'High (>103°F / >39.4°C)' }
          ],
          weight: 0.85
        },
        {
          id: 'h10',
          questionNumber: 10,
          text: 'What triggered or worsens your symptoms?',
          type: 'multiple_select',
          options: [
            { value: 'stress', label: 'Stress or emotional tension' },
            { value: 'lack_sleep', label: 'Lack of sleep' },
            { value: 'certain_foods', label: 'Certain foods or caffeine' },
            { value: 'weather', label: 'Weather changes' },
            { value: 'menstrual_cycle', label: 'Menstrual cycle (if applicable)' },
            { value: 'nothing_specific', label: 'Nothing specific' }
          ],
          weight: 0.65
        }
      ]
    },

    chest_lungs: {
      bodyPartId: 'chest_lungs',
      questions: [
        {
          id: 'c1',
          questionNumber: 1,
          text: 'What type of chest pain are you experiencing?',
          type: 'single_select',
          options: [
            { value: 'sharp', label: 'Sharp, stabbing pain' },
            { value: 'burning', label: 'Burning sensation' },
            { value: 'pressure', label: 'Pressure or tightness' },
            { value: 'aching', label: 'Dull aching' },
            { value: 'heaviness', label: 'Heaviness or fullness' }
          ],
          weight: 0.95
        },
        {
          id: 'c2',
          questionNumber: 2,
          text: 'When does the chest pain occur?',
          type: 'single_select',
          options: [
            { value: 'constant', label: 'Constant throughout the day' },
            { value: 'during_activity', label: 'During physical activity or exertion' },
            { value: 'at_rest', label: 'At rest or during sleep' },
            { value: 'after_meals', label: 'After meals' },
            { value: 'intermittent', label: 'Intermittent/random episodes' }
          ],
          weight: 0.9
        },
        {
          id: 'c3',
          questionNumber: 3,
          text: 'How long does each episode last?',
          type: 'single_select',
          options: [
            { value: 'seconds', label: 'Seconds' },
            { value: 'minutes', label: 'Minutes' },
            { value: 'hours', label: 'Hours' },
            { value: 'days', label: 'Days or longer' }
          ],
          weight: 0.8
        },
        {
          id: 'c4',
          questionNumber: 4,
          text: 'Do you have a cough?',
          type: 'single_select',
          options: [
            { value: 'no_cough', label: 'No cough' },
            { value: 'dry', label: 'Dry cough (no phlegm)' },
            { value: 'productive_clear', label: 'Productive cough - clear sputum' },
            { value: 'productive_yellow', label: 'Productive cough - yellow/greenish sputum' },
            { value: 'productive_blood', label: 'Cough with blood or rust-colored sputum' }
          ],
          weight: 0.85
        },
        {
          id: 'c5',
          questionNumber: 5,
          text: 'Are you experiencing breathing difficulty?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No breathing difficulty' },
            { value: 'mild', label: 'Mild - only during heavy exertion' },
            { value: 'moderate', label: 'Moderate - during normal activities' },
            { value: 'severe', label: 'Severe - even at rest' },
            { value: 'extreme', label: 'Extreme - waking up gasping for air' }
          ],
          weight: 0.9
        },
        {
          id: 'c6',
          questionNumber: 6,
          text: 'Do you have wheezing or whistling sounds when breathing?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No wheezing' },
            { value: 'during_exertion', label: 'Only during exertion' },
            { value: 'frequent', label: 'Frequent wheezing' },
            { value: 'constant', label: 'Constant wheezing' }
          ],
          weight: 0.8
        },
        {
          id: 'c7',
          questionNumber: 7,
          text: 'Are you experiencing heart palpitations?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No palpitations' },
            { value: 'occasional', label: 'Occasional - few times a week' },
            { value: 'frequent', label: 'Frequent - several times daily' },
            { value: 'rapid', label: 'Rapid heartbeat (tachycardia)' },
            { value: 'irregular', label: 'Irregular or skipped beats' }
          ],
          weight: 0.85
        },
        {
          id: 'c8',
          questionNumber: 8,
          text: 'Are you experiencing fatigue or weakness?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No fatigue' },
            { value: 'mild', label: 'Mild fatigue' },
            { value: 'moderate', label: 'Moderate - affecting activities' },
            { value: 'severe', label: 'Severe - very difficult to function' }
          ],
          weight: 0.75
        },
        {
          id: 'c9',
          questionNumber: 9,
          text: 'Do you have a history of any risk factors?',
          type: 'multiple_select',
          options: [
            { value: 'smoking', label: 'Smoking' },
            { value: 'diabetes', label: 'Diabetes' },
            { value: 'high_bp', label: 'High blood pressure' },
            { value: 'family_history', label: 'Family history of heart disease' },
            { value: 'obesity', label: 'Obesity' },
            { value: 'none', label: 'None of these' }
          ],
          weight: 0.8
        },
        {
          id: 'c10',
          questionNumber: 10,
          text: 'When did these symptoms start?',
          type: 'single_select',
          options: [
            { value: 'acute', label: 'Sudden onset (within 24 hours)' },
            { value: 'recent', label: 'Recent (past few days)' },
            { value: 'gradual', label: 'Gradual onset (past weeks)' },
            { value: 'chronic', label: 'Long-standing (months or years)' }
          ],
          weight: 0.7
        }
      ]
    },

    abdomen_digestive: {
      bodyPartId: 'abdomen_digestive',
      questions: [
        {
          id: 'a1',
          questionNumber: 1,
          text: 'What type of abdominal pain are you experiencing?',
          type: 'single_select',
          options: [
            { value: 'sharp', label: 'Sharp, stabbing pain' },
            { value: 'cramping', label: 'Cramping (like muscle spasms)' },
            { value: 'dull', label: 'Dull, persistent ache' },
            { value: 'burning', label: 'Burning sensation' },
            { value: 'bloating', label: 'Bloating/distension pain' }
          ],
          weight: 0.85
        },
        {
          id: 'a2',
          questionNumber: 2,
          text: 'Where is the pain located in your abdomen?',
          type: 'single_select',
          options: [
            { value: 'upper_right', label: 'Upper right (liver/gallbladder area)' },
            { value: 'upper_left', label: 'Upper left (spleen area)' },
            { value: 'upper_center', label: 'Upper center (stomach area)' },
            { value: 'lower_right', label: 'Lower right (appendix area)' },
            { value: 'lower_left', label: 'Lower left (colon area)' },
            { value: 'lower_center', label: 'Lower center (intestines)' },
            { value: 'generalized', label: 'Generalized throughout abdomen' }
          ],
          weight: 0.9
        },
        {
          id: 'a3',
          questionNumber: 3,
          text: 'Do you have nausea or vomiting?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No nausea or vomiting' },
            { value: 'nausea_only', label: 'Nausea without vomiting' },
            { value: 'occasional_vomiting', label: 'Occasional vomiting' },
            { value: 'frequent_vomiting', label: 'Frequent vomiting' },
            { value: 'vomiting_blood', label: 'Vomiting blood or dark material' }
          ],
          weight: 0.9
        },
        {
          id: 'a4',
          questionNumber: 4,
          text: 'What is your bowel movement pattern?',
          type: 'single_select',
          options: [
            { value: 'normal', label: 'Normal (regular bowel movements)' },
            { value: 'constipation', label: 'Constipation (infrequent/hard stools)' },
            { value: 'diarrhea_occasional', label: 'Occasional diarrhea' },
            { value: 'diarrhea_frequent', label: 'Frequent diarrhea' },
            { value: 'alternating', label: 'Alternating between constipation and diarrhea' }
          ],
          weight: 0.85
        },
        {
          id: 'a5',
          questionNumber: 5,
          text: 'Do you have any stool abnormalities?',
          type: 'multiple_select',
          options: [
            { value: 'blood', label: 'Blood in stool' },
            { value: 'black_tar', label: 'Black, tar-like stools' },
            { value: 'pale', label: 'Pale or clay-colored stools' },
            { value: 'mucus', label: 'Mucus in stool' },
            { value: 'greasy', label: 'Greasy or oily stools' },
            { value: 'none', label: 'None of these' }
          ],
          weight: 0.8
        },
        {
          id: 'a6',
          questionNumber: 6,
          text: 'Are you experiencing bloating or gas?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No bloating or gas' },
            { value: 'mild', label: 'Mild bloating/gas' },
            { value: 'moderate', label: 'Moderate - noticeable discomfort' },
            { value: 'severe', label: 'Severe - very uncomfortable' }
          ],
          weight: 0.7
        },
        {
          id: 'a7',
          questionNumber: 7,
          text: 'Do you have heartburn or acid reflux?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'Never' },
            { value: 'occasional', label: 'Occasionally (few times a month)' },
            { value: 'frequent', label: 'Frequent (several times a week)' },
            { value: 'daily', label: 'Daily' }
          ],
          weight: 0.75
        },
        {
          id: 'a8',
          questionNumber: 8,
          text: 'Are you experiencing loss of appetite?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'Normal appetite' },
            { value: 'mild', label: 'Slightly reduced appetite' },
            { value: 'moderate', label: 'Significantly reduced appetite' },
            { value: 'severe', label: 'Cannot eat anything' }
          ],
          weight: 0.7
        },
        {
          id: 'a9',
          questionNumber: 9,
          text: 'Have you experienced recent weight changes?',
          type: 'single_select',
          options: [
            { value: 'stable', label: 'Weight stable' },
            { value: 'gain', label: 'Weight gain' },
            { value: 'loss_slight', label: 'Slight weight loss (< 5 lbs)' },
            { value: 'loss_moderate', label: 'Moderate weight loss (5-10 lbs)' },
            { value: 'loss_severe', label: 'Significant weight loss (> 10 lbs)' }
          ],
          weight: 0.75
        },
        {
          id: 'a10',
          questionNumber: 10,
          text: 'What other symptoms are you experiencing?',
          type: 'multiple_select',
          options: [
            { value: 'fever', label: 'Fever' },
            { value: 'chills', label: 'Chills' },
            { value: 'joint_pain', label: 'Joint pain' },
            { value: 'yellow_skin', label: 'Yellowing of skin/eyes' },
            { value: 'dark_urine', label: 'Dark urine' },
            { value: 'none', label: 'None of these' }
          ],
          weight: 0.75
        }
      ]
    },

    limbs_joints: {
      bodyPartId: 'limbs_joints',
      questions: [
        {
          id: 'l1',
          questionNumber: 1,
          text: 'Which joint(s) are affected?',
          type: 'multiple_select',
          options: [
            { value: 'shoulders', label: 'Shoulders' },
            { value: 'elbows', label: 'Elbows' },
            { value: 'wrists', label: 'Wrists' },
            { value: 'hands', label: 'Hands/fingers' },
            { value: 'hips', label: 'Hips' },
            { value: 'knees', label: 'Knees' },
            { value: 'ankles', label: 'Ankles' },
            { value: 'feet', label: 'Feet/toes' }
          ],
          weight: 0.85
        },
        {
          id: 'l2',
          questionNumber: 2,
          text: 'What type of pain are you experiencing?',
          type: 'single_select',
          options: [
            { value: 'sharp', label: 'Sharp, shooting pain' },
            { value: 'aching', label: 'Aching or throbbing' },
            { value: 'burning', label: 'Burning sensation' },
            { value: 'stiffness', label: 'Stiffness or reduced mobility' }
          ],
          weight: 0.8
        },
        {
          id: 'l3',
          questionNumber: 3,
          text: 'Is the pain on one side or both sides?',
          type: 'single_select',
          options: [
            { value: 'unilateral', label: 'One side (left or right)' },
            { value: 'bilateral', label: 'Both sides (symmetrical)' },
            { value: 'alternating', label: 'Alternating between sides' }
          ],
          weight: 0.75
        },
        {
          id: 'l4',
          questionNumber: 4,
          text: 'Do your joints have any swelling?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No swelling' },
            { value: 'mild', label: 'Mild swelling' },
            { value: 'moderate', label: 'Moderate swelling' },
            { value: 'severe', label: 'Severe/visible swelling' }
          ],
          weight: 0.8
        },
        {
          id: 'l5',
          questionNumber: 5,
          text: 'Is the affected area warm or red?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'Normal appearance and temperature' },
            { value: 'warm', label: 'Warm to touch' },
            { value: 'red', label: 'Red/inflamed appearance' },
            { value: 'both', label: 'Both warm and red' }
          ],
          weight: 0.8
        },
        {
          id: 'l6',
          questionNumber: 6,
          text: 'How is your range of motion?',
          type: 'single_select',
          options: [
            { value: 'full', label: 'Full range of motion' },
            { value: 'slightly_limited', label: 'Slightly limited' },
            { value: 'moderately_limited', label: 'Moderately limited' },
            { value: 'very_limited', label: 'Severely limited or cannot move' }
          ],
          weight: 0.85
        },
        {
          id: 'l7',
          questionNumber: 7,
          text: 'When is the pain worse?',
          type: 'single_select',
          options: [
            { value: 'morning', label: 'Morning (upon waking)' },
            { value: 'end_of_day', label: 'End of day (after activity)' },
            { value: 'during_activity', label: 'During physical activity' },
            { value: 'at_rest', label: 'At rest' },
            { value: 'constant', label: 'Constant throughout day' }
          ],
          weight: 0.75
        },
        {
          id: 'l8',
          questionNumber: 8,
          text: 'Did this start after an injury or trauma?',
          type: 'single_select',
          options: [
            { value: 'no_injury', label: 'No recent injury' },
            { value: 'minor', label: 'Minor injury or strain' },
            { value: 'moderate', label: 'Moderate injury' },
            { value: 'severe', label: 'Severe injury or fall' }
          ],
          weight: 0.85
        },
        {
          id: 'l9',
          questionNumber: 9,
          text: 'Do you have any muscle weakness or numbness?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No weakness or numbness' },
            { value: 'mild_weakness', label: 'Mild weakness' },
            { value: 'numbness', label: 'Numbness/tingling (paresthesia)' },
            { value: 'both', label: 'Both weakness and numbness' }
          ],
          weight: 0.8
        },
        {
          id: 'l10',
          questionNumber: 10,
          text: 'How long have you had these symptoms?',
          type: 'single_select',
          options: [
            { value: 'acute', label: 'Acute (0-2 weeks)' },
            { value: 'subacute', label: 'Subacute (2-8 weeks)' },
            { value: 'chronic', label: 'Chronic (> 8 weeks)' }
          ],
          weight: 0.7
        }
      ]
    },

    skin_surface: {
      bodyPartId: 'skin_surface',
      questions: [
        {
          id: 's1',
          questionNumber: 1,
          text: 'What is the primary skin problem?',
          type: 'single_select',
          options: [
            { value: 'rash', label: 'Rash/hives' },
            { value: 'itching', label: 'Severe itching' },
            { value: 'lesions', label: 'Open lesions or sores' },
            { value: 'discoloration', label: 'Discoloration or dark spots' },
            { value: 'dryness', label: 'Extreme dryness or scaling' }
          ],
          weight: 0.85
        },
        {
          id: 's2',
          questionNumber: 2,
          text: 'What does the rash look like?',
          type: 'multiple_select',
          options: [
            { value: 'red_bumps', label: 'Red bumps/papules' },
            { value: 'blisters', label: 'Blisters or vesicles' },
            { value: 'pustules', label: 'Pus-filled pustules' },
            { value: 'patches', label: 'Large patches or plaques' },
            { value: 'scaly', label: 'Scaly/dry appearance' },
            { value: 'oozing', label: 'Oozing or crusting' }
          ],
          weight: 0.9
        },
        {
          id: 's3',
          questionNumber: 3,
          text: 'What color is the rash?',
          type: 'single_select',
          options: [
            { value: 'red', label: 'Bright red' },
            { value: 'pink', label: 'Pink' },
            { value: 'purple', label: 'Purple or dark red' },
            { value: 'skin_colored', label: 'Same color as skin' },
            { value: 'brown', label: 'Brown or tan' }
          ],
          weight: 0.75
        },
        {
          id: 's4',
          questionNumber: 4,
          text: 'Where is the rash located?',
          type: 'multiple_select',
          options: [
            { value: 'face', label: 'Face' },
            { value: 'arms', label: 'Arms' },
            { value: 'legs', label: 'Legs' },
            { value: 'trunk', label: 'Trunk/back' },
            { value: 'genitals', label: 'Genital area' },
            { value: 'scalp', label: 'Scalp/hair' },
            { value: 'generalized', label: 'Generalized over body' }
          ],
          weight: 0.8
        },
        {
          id: 's5',
          questionNumber: 5,
          text: 'How intense is the itching?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No itching' },
            { value: 'mild', label: 'Mild itching' },
            { value: 'moderate', label: 'Moderate - affects sleep/activities' },
            { value: 'severe', label: 'Severe - unbearable, constant scratching' }
          ],
          weight: 0.85
        },
        {
          id: 's6',
          questionNumber: 6,
          text: 'Is the rash spreading or changing?',
          type: 'single_select',
          options: [
            { value: 'localized', label: 'Localized (not spreading)' },
            { value: 'slowly_spreading', label: 'Slowly spreading' },
            { value: 'rapidly_spreading', label: 'Rapidly spreading' },
            { value: 'changing_appearance', label: 'Changing in appearance/color' }
          ],
          weight: 0.8
        },
        {
          id: 's7',
          questionNumber: 7,
          text: 'Do you have any discharge or drainage?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No discharge' },
            { value: 'clear', label: 'Clear/serous fluid' },
            { value: 'yellow', label: 'Yellow/purulent fluid' },
            { value: 'blood', label: 'Bloody or blood-tinged' }
          ],
          weight: 0.8
        },
        {
          id: 's8',
          questionNumber: 8,
          text: 'Is there any odor from the affected area?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No odor' },
            { value: 'slight', label: 'Slight odor' },
            { value: 'moderate', label: 'Moderate/unpleasant odor' },
            { value: 'severe', label: 'Strong/foul odor (possible infection)' }
          ],
          weight: 0.75
        },
        {
          id: 's9',
          questionNumber: 9,
          text: 'Have you had any recent exposure or triggers?',
          type: 'multiple_select',
          options: [
            { value: 'new_product', label: 'New skincare/cleaning product' },
            { value: 'allergy', label: 'Known allergen exposure' },
            { value: 'animals', label: 'Contact with animals' },
            { value: 'plants', label: 'Contact with plants (poison ivy, etc.)' },
            { value: 'sun_exposure', label: 'Sun exposure' },
            { value: 'insect_bite', label: 'Insect bites' }
          ],
          weight: 0.75
        },
        {
          id: 's10',
          questionNumber: 10,
          text: 'How long have you had this skin condition?',
          type: 'single_select',
          options: [
            { value: 'acute', label: 'Acute (0-2 weeks)' },
            { value: 'subacute', label: 'Subacute (2-8 weeks)' },
            { value: 'chronic', label: 'Chronic (months to years)' }
          ],
          weight: 0.7
        }
      ]
    },

    general_systemic: {
      bodyPartId: 'general_systemic',
      questions: [
        {
          id: 'g1',
          questionNumber: 1,
          text: 'Do you have a fever?',
          type: 'single_select',
          options: [
            { value: 'no_fever', label: 'No fever (98.6°F / 37°C)' },
            { value: 'low', label: 'Low-grade (99-101°F / 37.2-38.3°C)' },
            { value: 'moderate', label: 'Moderate (101-103°F / 38.3-39.4°C)' },
            { value: 'high', label: 'High (>103°F / >39.4°C)' }
          ],
          weight: 0.9
        },
        {
          id: 'g2',
          questionNumber: 2,
          text: 'How is your energy level?',
          type: 'single_select',
          options: [
            { value: 'normal', label: 'Normal energy' },
            { value: 'mild_fatigue', label: 'Mild fatigue' },
            { value: 'moderate_fatigue', label: 'Moderate fatigue - affecting daily activities' },
            { value: 'severe_fatigue', label: 'Severe fatigue - can barely get out of bed' }
          ],
          weight: 0.85
        },
        {
          id: 'g3',
          questionNumber: 3,
          text: 'Are you experiencing body aches?',
          type: 'single_select',
          options: [
            { value: 'none', label: 'No body aches' },
            { value: 'mild', label: 'Mild aches' },
            { value: 'moderate', label: 'Moderate muscle/joint aches' },
            { value: 'severe', label: 'Severe pain throughout body' }
          ],
          weight: 0.8
        },
        {
          id: 'g4',
          questionNumber: 4,
          text: 'Do you have chills or sweating?',
          type: 'single_select',
          options: [
            { value: 'neither', label: 'Neither chills nor sweating' },
            { value: 'chills', label: 'Chills (feeling cold)' },
            { value: 'sweating', label: 'Sweating (especially at night)' },
            { value: 'both', label: 'Both chills and sweating' }
          ],
          weight: 0.8
        },
        {
          id: 'g5',
          questionNumber: 5,
          text: 'How is your appetite?',
          type: 'single_select',
          options: [
            { value: 'normal', label: 'Normal appetite' },
            { value: 'decreased', label: 'Decreased appetite' },
            { value: 'significantly_reduced', label: 'Significantly reduced' },
            { value: 'cannot_eat', label: 'Cannot eat anything' }
          ],
          weight: 0.75
        },
        {
          id: 'g6',
          questionNumber: 6,
          text: 'What is your current hydration status?',
          type: 'single_select',
          options: [
            { value: 'well_hydrated', label: 'Well hydrated - normal thirst' },
            { value: 'increased_thirst', label: 'Increased thirst' },
            { value: 'excessive_thirst', label: 'Excessive thirst' },
            { value: 'dehydrated', label: 'Dehydrated - unable to keep fluids down' }
          ],
          weight: 0.8
        },
        {
          id: 'g7',
          questionNumber: 7,
          text: 'Are you experiencing any respiratory symptoms?',
          type: 'multiple_select',
          options: [
            { value: 'none', label: 'No respiratory symptoms' },
            { value: 'runny_nose', label: 'Runny or stuffy nose' },
            { value: 'sore_throat', label: 'Sore throat' },
            { value: 'cough', label: 'Cough' },
            { value: 'sneezing', label: 'Sneezing' }
          ],
          weight: 0.75
        },
        {
          id: 'g8',
          questionNumber: 8,
          text: 'Have you had a recent illness or infection?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No recent illness' },
            { value: 'minor', label: 'Minor illness (cold)' },
            { value: 'moderate', label: 'Moderate illness (flu)' },
            { value: 'severe', label: 'Severe illness or infection' }
          ],
          weight: 0.7
        },
        {
          id: 'g9',
          questionNumber: 9,
          text: 'Do you have swollen lymph nodes?',
          type: 'single_select',
          options: [
            { value: 'no', label: 'No swelling' },
            { value: 'mild', label: 'Mildly swollen' },
            { value: 'visible', label: 'Visibly swollen' },
            { value: 'severe', label: 'Very swollen and painful' }
          ],
          weight: 0.75
        },
        {
          id: 'g10',
          questionNumber: 10,
          text: 'When did your symptoms start?',
          type: 'single_select',
          options: [
            { value: 'acute', label: 'Acute - sudden onset (0-48 hours)' },
            { value: 'recent', label: 'Recent onset (2-7 days)' },
            { value: 'gradual', label: 'Gradual onset (1-4 weeks)' },
            { value: 'chronic', label: 'Chronic (> 4 weeks)' }
          ],
          weight: 0.7
        }
      ]
    }
  },

  // ============================================
  // STAGE 3: DISEASE DIAGNOSIS MAPPING
  // ============================================
  diseaseMapping: {
    'Migraine': {
      id: 'migraine',
      name: 'Migraine',
      bodyParts: ['head_neck'],
      symptoms: ['h1:throbbing', 'h3:moderate', 'h3:severe', 'h5:nausea', 'h5:sensitivity_light'],
      severity: 'moderate',
      symptoms_count: 3,
      accuracy: 0.82,
      description: 'Intense, often one-sided headache with throbbing pain, nausea, and sensitivity to light.'
    },
    'Tension Headache': {
      id: 'tension_headache',
      name: 'Tension Headache',
      bodyParts: ['head_neck'],
      symptoms: ['h1:tension', 'h2:generalized', 'h3:mild', 'h4:frequent'],
      severity: 'mild',
      symptoms_count: 2,
      accuracy: 0.85,
      description: 'Dull, pressure-like pain across the head, often caused by stress and muscle tension.'
    },
    'Common Cold': {
      id: 'common_cold',
      name: 'Common Cold (Upper Respiratory Infection)',
      bodyParts: ['head_neck', 'general_systemic'],
      symptoms: ['h6:mild', 'g7:runny_nose', 'g7:sneezing', 'h9:low_grade', 'g1:low'],
      severity: 'mild',
      symptoms_count: 3,
      accuracy: 0.88,
      description: 'Viral infection causing runny nose, sneezing, sore throat, and mild fever.'
    },
    'Asthma': {
      id: 'asthma',
      name: 'Asthma',
      bodyParts: ['chest_lungs'],
      symptoms: ['c5:moderate', 'c5:severe', 'c6:frequent', 'c6:constant', 'c2:during_activity'],
      severity: 'moderate',
      symptoms_count: 3,
      accuracy: 0.79,
      description: 'Chronic respiratory condition causing breathing difficulty, wheezing, and chest tightness.'
    },
    'Pneumonia': {
      id: 'pneumonia',
      name: 'Pneumonia (Bacterial)',
      bodyParts: ['chest_lungs', 'general_systemic'],
      symptoms: ['c4:productive_yellow', 'c5:severe', 'g1:high', 'g3:severe', 'c1:sharp'],
      severity: 'severe',
      symptoms_count: 4,
      accuracy: 0.86,
      description: 'Serious lung infection causing productive cough, high fever, severe chest pain, and difficulty breathing.'
    },
    'Gastroenteritis': {
      id: 'gastroenteritis',
      name: 'Gastroenteritis (Food Poisoning)',
      bodyParts: ['abdomen_digestive', 'general_systemic'],
      symptoms: ['a1:cramping', 'a3:frequent_vomiting', 'a4:diarrhea_frequent', 'g1:moderate', 'g5:cannot_eat'],
      severity: 'moderate',
      symptoms_count: 4,
      accuracy: 0.81,
      description: 'Stomach and intestinal inflammation causing vomiting, diarrhea, abdominal cramping, and fever.'
    },
    'Arthritis': {
      id: 'arthritis',
      name: 'Osteoarthritis/Rheumatoid Arthritis',
      bodyParts: ['limbs_joints'],
      symptoms: ['l1:multiple', 'l2:aching', 'l4:moderate', 'l4:severe', 'l6:moderately_limited', 'l7:morning'],
      severity: 'moderate',
      symptoms_count: 4,
      accuracy: 0.78,
      description: 'Joint inflammation causing chronic pain, stiffness (especially morning), swelling, and limited mobility.'
    },
    'Skin Allergy/Dermatitis': {
      id: 'skin_allergy',
      name: 'Allergic Reaction/Contact Dermatitis',
      bodyParts: ['skin_surface'],
      symptoms: ['s1:rash', 's2:red_bumps', 's5:moderate', 's5:severe', 's9:new_product', 's9:allergy'],
      severity: 'mild',
      symptoms_count: 3,
      accuracy: 0.83,
      description: 'Allergic skin reaction causing red bumps, intense itching, and rash after exposure to allergen.'
    },
    'Eczema': {
      id: 'eczema',
      name: 'Eczema (Atopic Dermatitis)',
      bodyParts: ['skin_surface'],
      symptoms: ['s1:dryness', 's2:scaly', 's5:severe', 's10:chronic', 's3:red', 's2:red_bumps'],
      severity: 'mild',
      symptoms_count: 3,
      accuracy: 0.80,
      description: 'Chronic inflammatory skin condition causing extreme dryness, intense itching, and scaly red patches.'
    },
    'Flu (Influenza)': {
      id: 'flu',
      name: 'Influenza (Flu)',
      bodyParts: ['general_systemic', 'head_neck'],
      symptoms: ['g1:high', 'g3:severe', 'g4:both', 'g2:severe_fatigue', 'h9:high', 'h1:throbbing'],
      severity: 'moderate',
      symptoms_count: 4,
      accuracy: 0.84,
      description: 'Viral infection causing high fever, body aches, chills, severe fatigue, and headache.'
    },
    'Urinary Tract Infection': {
      id: 'uti',
      name: 'Urinary Tract Infection (UTI)',
      bodyParts: ['abdomen_digestive', 'general_systemic'],
      symptoms: ['a1:burning', 'a3:nausea_only', 'g1:low', 'g6:increased_thirst'],
      severity: 'mild',
      symptoms_count: 3,
      accuracy: 0.82,
      description: 'Bacterial infection of urinary system causing painful urination, burning sensation, and increased urinary frequency.'
    }
  },

  // ============================================
  // STAGE 4: USER RESPONSE STORAGE & ANALYSIS
  // ============================================
  userSession: {
    sessionId: null,
    userId: null,
    createdAt: null,
    selectedBodyPart: null,
    questionnaire_responses: {}, // { questionId: selectedValue }
    symptomScore: 0,
    diseasePredictions: [], // Array of predicted diseases with confidence scores
    topPredictions: [], // Top 3 predictions
    analysis: null,
    recommendedActions: []
  },

  // ============================================
  // METHODS: Core Logic
  // ============================================
  methods: {
    /**
     * Initialize a new user session
     */
    initializeSession: function(userId) {
      const sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      this.userSession = {
        sessionId: sessionId,
        userId: userId,
        createdAt: new Date().toISOString(),
        selectedBodyPart: null,
        questionnaire_responses: {},
        symptomScore: 0,
        diseasePredictions: [],
        topPredictions: [],
        analysis: null,
        recommendedActions: []
      };
      return this.userSession;
    },

    /**
     * Set the selected body part and return relevant questions
     */
    selectBodyPart: function(bodyPartId) {
      if (!this.bodyParts[bodyPartId]) {
        return { error: 'Invalid body part selected' };
      }

      this.userSession.selectedBodyPart = bodyPartId;
      const questions = this.symptoms[bodyPartId].questions;

      return {
        bodyPartId: bodyPartId,
        bodyPartLabel: this.bodyParts[bodyPartId].label,
        description: this.bodyParts[bodyPartId].description,
        totalQuestions: questions.length,
        questions: questions
      };
    },

    /**
     * Record user's answer to a question
     */
    recordAnswer: function(questionId, selectedValue) {
      this.userSession.questionnaire_responses[questionId] = {
        value: selectedValue,
        timestamp: new Date().toISOString()
      };
      return true;
    },

    /**
     * Calculate symptom score based on answers
     */
    calculateSymptomScore: function() {
      let totalScore = 0;
      const bodyPartId = this.userSession.selectedBodyPart;
      const bodyPartSymptoms = this.symptoms[bodyPartId];

      for (const question of bodyPartSymptoms.questions) {
        if (this.userSession.questionnaire_responses[question.id]) {
          const weight = question.weight || 0.5;
          totalScore += weight;
        }
      }

      this.userSession.symptomScore = (totalScore / 10) * 100; // Normalize to 100
      return this.userSession.symptomScore;
    },

    /**
     * Predict diseases based on user responses
     * (Integration point for ML model)
     */
    predictDiseases: function() {
      const predictions = [];
      const bodyPartId = this.userSession.selectedBodyPart;
      const userResponses = this.userSession.questionnaire_responses;

      // Matching algorithm: Count symptom matches with disease patterns
      for (const [diseaseKey, diseaseData] of Object.entries(this.diseaseMapping)) {
        if (diseaseData.bodyParts.includes(bodyPartId)) {
          let matchCount = 0;
          const matchedSymptoms = [];

          // Check how many symptoms match
          for (const symptomPattern of diseaseData.symptoms) {
            const [questionId, expectedValue] = symptomPattern.split(':');
            const userAnswer = userResponses[questionId];

            if (userAnswer && userAnswer.value === expectedValue) {
              matchCount++;
              matchedSymptoms.push(questionId);
            } else if (userAnswer && questionId.startsWith('h') || questionId.startsWith('c') || questionId.startsWith('a')) {
              // Partial matching for related values
              matchCount += 0.5;
            }
          }

          // Calculate confidence score (0-100)
          const confidenceScore = (matchCount / diseaseData.symptoms.length) * 100;

          if (confidenceScore > 20) { // Only include if >20% confidence
            predictions.push({
              disease: diseaseData.name,
              diseaseId: diseaseData.id,
              confidence: Math.min(confidenceScore, 99), // Cap at 99%
              matchedSymptoms: matchedSymptoms.length,
              totalSymptoms: diseaseData.symptoms.length,
              severity: diseaseData.severity,
              description: diseaseData.description,
              accuracy: diseaseData.accuracy
            });
          }
        }
      }

      // Sort by confidence score (descending)
      predictions.sort((a, b) => b.confidence - a.confidence);

      // Get top 3 predictions
      this.userSession.diseasePredictions = predictions;
      this.userSession.topPredictions = predictions.slice(0, 3);

      return this.userSession.topPredictions;
    },

    /**
     * Generate comprehensive health analysis
     */
    generateAnalysis: function() {
      const topDiseases = this.userSession.topPredictions;
      const symptomScore = this.userSession.symptomScore;
      const bodyPart = this.bodyParts[this.userSession.selectedBodyPart];

      let analysis = {
        generatedAt: new Date().toISOString(),
        bodyPartFocused: bodyPart.label,
        symptomSeverity: this.calculateSeverity(symptomScore),
        overallRisk: this.calculateRisk(topDiseases),
        topDiagnoses: topDiseases,
        summary: ''
      };

      // Generate summary text
      if (topDiseases.length > 0) {
        analysis.summary = `Based on your responses, the most likely conditions affecting your ${bodyPart.label} are:
1. ${topDiseases[0].disease} (${topDiseases[0].confidence.toFixed(1)}% confidence)
2. ${topDiseases[1]?.disease || 'None'} (${topDiseases[1]?.confidence.toFixed(1) || 'N/A'}% confidence)
3. ${topDiseases[2]?.disease || 'None'} (${topDiseases[2]?.confidence.toFixed(1) || 'N/A'}% confidence)

These predictions are based on symptom matching and should NOT replace professional medical advice.`;
      }

      this.userSession.analysis = analysis;
      return analysis;
    },

    /**
     * Generate recommended actions
     */
    generateRecommendations: function() {
      const topDisease = this.userSession.topPredictions[0];
      const recommendations = {
        immediate: [],
        primary_care: [],
        specialist: [],
        self_care: [],
        when_to_seek_emergency: []
      };

      if (!topDisease) return recommendations;

      // Generic recommendations based on disease severity
      if (topDisease.severity === 'severe') {
        recommendations.when_to_seek_emergency = [
          'Seek immediate medical attention if symptoms worsen',
          'Visit nearest emergency room if experiencing severe pain or difficulty breathing',
          'Call ambulance if unable to reach hospital'
        ];
      }

      recommendations.primary_care = [
        `Consult a healthcare provider for proper diagnosis of ${topDisease.disease}`,
        'Get necessary tests (blood work, imaging if recommended)',
        'Follow prescribed treatment plan'
      ];

      recommendations.self_care = [
        'Rest and stay hydrated',
        'Maintain a symptom diary',
        'Avoid triggers if identified',
        'Take over-the-counter medications as needed (consult doctor first)'
      ];

      recommendations.specialist = [
        `If diagnosis is confirmed, you may need specialist consultation based on the condition`
      ];

      this.userSession.recommendedActions = recommendations;
      return recommendations;
    },

    /**
     * Calculate severity level
     */
    calculateSeverity: function(score) {
      if (score < 25) return 'Mild';
      if (score < 50) return 'Moderate';
      if (score < 75) return 'Significant';
      return 'Severe';
    },

    /**
     * Calculate overall risk level
     */
    calculateRisk: function(predictions) {
      if (predictions.length === 0) return 'Low';
      const topConfidence = predictions[0].confidence;
      const topSeverity = predictions[0].severity;

      if (topSeverity === 'severe' && topConfidence > 70) return 'High';
      if (topSeverity === 'moderate' && topConfidence > 60) return 'Medium';
      return 'Low';
    },

    /**
     * Get full questionnaire report
     */
    getFullReport: function() {
      return {
        session: this.userSession,
        questions_answered: Object.keys(this.userSession.questionnaire_responses).length,
        total_questions: 10,
        completion_percentage: (Object.keys(this.userSession.questionnaire_responses).length / 10) * 100,
        symptom_score: this.userSession.symptomScore,
        predictions: this.userSession.diseasePredictions,
        analysis: this.userSession.analysis,
        recommendations: this.userSession.recommendedActions,
        disclaimer: 'This tool is for informational purposes only. It does not replace professional medical diagnosis. Always consult a healthcare provider for medical advice.'
      };
    },

    /**
     * Reset session for new user
     */
    resetSession: function() {
      this.userSession = {
        sessionId: null,
        userId: null,
        createdAt: null,
        selectedBodyPart: null,
        questionnaire_responses: {},
        symptomScore: 0,
        diseasePredictions: [],
        topPredictions: [],
        analysis: null,
        recommendedActions: []
      };
      return { message: 'Session reset successfully' };
    }
  }
};

// ============================================
// EXPORT & USAGE EXAMPLE
// ============================================
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DiseasePredictionSystem;
}

